/**
 * Position evaluation functions for chess AI
 * 
 * Evaluates positions from white's perspective (positive = good for white, negative = good for black)
 */

import { GameState, PieceType, Color } from './types';

// Standard piece values in centipawns (1 pawn = 100)
export const PIECE_VALUES: Record<PieceType, number> = {
  [PieceType.Pawn]: 100,
  [PieceType.Knight]: 320,
  [PieceType.Bishop]: 330,
  [PieceType.Rook]: 500,
  [PieceType.Queen]: 900,
  [PieceType.King]: 0, // King has no material value (game ends if captured)
};

// Piece-square tables: bonus points for placing pieces on good squares
// Tables are from white's perspective (rank 0 = white's back rank)
// Values in centipawns

const PAWN_TABLE = [
  0,  0,  0,  0,  0,  0,  0,  0,  // Rank 0 (shouldn't happen - pawns can't be here)
  5, 10, 10,-20,-20, 10, 10,  5,  // Rank 1 (starting position)
  5, -5,-10,  0,  0,-10, -5,  5,  // Rank 2
  0,  0,  0, 20, 20,  0,  0,  0,  // Rank 3
  5,  5, 10, 25, 25, 10,  5,  5,  // Rank 4 (center control)
  10, 10, 20, 30, 30, 20, 10, 10, // Rank 5 (advanced)
  50, 50, 50, 50, 50, 50, 50, 50, // Rank 6 (7th rank, near promotion!)
  0,  0,  0,  0,  0,  0,  0,  0   // Rank 7 (promotion rank)
];

const KNIGHT_TABLE = [
  -50,-40,-30,-30,-30,-30,-40,-50,
  -40,-20,  0,  0,  0,  0,-20,-40,
  -30,  0, 10, 15, 15, 10,  0,-30,
  -30,  5, 15, 20, 20, 15,  5,-30,
  -30,  0, 15, 20, 20, 15,  0,-30,
  -30,  5, 10, 15, 15, 10,  5,-30,
  -40,-20,  0,  5,  5,  0,-20,-40,
  -50,-40,-30,-30,-30,-30,-40,-50
];

const BISHOP_TABLE = [
  -20,-10,-10,-10,-10,-10,-10,-20,
  -10,  0,  0,  0,  0,  0,  0,-10,
  -10,  0,  5, 10, 10,  5,  0,-10,
  -10,  5,  5, 10, 10,  5,  5,-10,
  -10,  0, 10, 10, 10, 10,  0,-10,
  -10, 10, 10, 10, 10, 10, 10,-10,
  -10,  5,  0,  0,  0,  0,  5,-10,
  -20,-10,-10,-10,-10,-10,-10,-20
];

const ROOK_TABLE = [
  0,  0,  0,  0,  0,  0,  0,  0,
  5, 10, 10, 10, 10, 10, 10,  5,
 -5,  0,  0,  0,  0,  0,  0, -5,
 -5,  0,  0,  0,  0,  0,  0, -5,
 -5,  0,  0,  0,  0,  0,  0, -5,
 -5,  0,  0,  0,  0,  0,  0, -5,
 -5,  0,  0,  0,  0,  0,  0, -5,
  0,  0,  0,  5,  5,  0,  0,  0
];

const QUEEN_TABLE = [
  -20,-10,-10, -5, -5,-10,-10,-20,
  -10,  0,  0,  0,  0,  0,  0,-10,
  -10,  0,  5,  5,  5,  5,  0,-10,
   -5,  0,  5,  5,  5,  5,  0, -5,
    0,  0,  5,  5,  5,  5,  0, -5,
  -10,  5,  5,  5,  5,  5,  0,-10,
  -10,  0,  5,  0,  0,  0,  0,-10,
  -20,-10,-10, -5, -5,-10,-10,-20
];

const KING_MIDDLEGAME_TABLE = [
  -30,-40,-40,-50,-50,-40,-40,-30,
  -30,-40,-40,-50,-50,-40,-40,-30,
  -30,-40,-40,-50,-50,-40,-40,-30,
  -30,-40,-40,-50,-50,-40,-40,-30,
  -20,-30,-30,-40,-40,-30,-30,-20,
  -10,-20,-20,-20,-20,-20,-20,-10,
   20, 20,  0,  0,  0,  0, 20, 20,
   20, 30, 10,  0,  0, 10, 30, 20
];

const KING_ENDGAME_TABLE = [
  -50,-40,-30,-20,-20,-30,-40,-50,
  -30,-20,-10,  0,  0,-10,-20,-30,
  -30,-10, 20, 30, 30, 20,-10,-30,
  -30,-10, 30, 40, 40, 30,-10,-30,
  -30,-10, 30, 40, 40, 30,-10,-30,
  -30,-10, 20, 30, 30, 20,-10,-30,
  -30,-30,  0,  0,  0,  0,-30,-30,
  -50,-30,-30,-30,-30,-30,-30,-50
];

/**
 * Gets piece-square table value for a piece at a given square
 * @param pieceType Type of piece
 * @param square Square index (0-63)
 * @param color Color of the piece
 * @param isEndgame Whether position is in endgame phase
 * @returns Positional bonus in centipawns
 */
function getPieceSquareValue(
  pieceType: PieceType,
  square: number,
  color: Color,
  isEndgame: boolean
): number {
  // Get the table for this piece type
  let table: number[];
  
  switch (pieceType) {
    case PieceType.Pawn:
      table = PAWN_TABLE;
      break;
    case PieceType.Knight:
      table = KNIGHT_TABLE;
      break;
    case PieceType.Bishop:
      table = BISHOP_TABLE;
      break;
    case PieceType.Rook:
      table = ROOK_TABLE;
      break;
    case PieceType.Queen:
      table = QUEEN_TABLE;
      break;
    case PieceType.King:
      table = isEndgame ? KING_ENDGAME_TABLE : KING_MIDDLEGAME_TABLE;
      break;
  }
  
  // For black pieces, flip the square vertically (black's perspective)
  const adjustedSquare = color === Color.White ? square : 63 - square;
  
  return table[adjustedSquare];
}

/**
 * Determines if position is in endgame phase
 * Endgame if:
 * - Both sides have no queens, OR
 * - Every side which has a queen has additionally no other pieces or one minorpiece maximum
 * @param state Current game state
 * @returns True if in endgame phase
 */
function isEndgame(state: GameState): boolean {
  let whiteQueens = 0;
  let blackQueens = 0;
  let whiteMinorPieces = 0; // Knights and bishops
  let blackMinorPieces = 0;
  let whiteRooks = 0;
  let blackRooks = 0;
  
  for (let square = 0; square < 64; square++) {
    const piece = state.board[square];
    if (!piece) continue;
    
    if (piece.color === Color.White) {
      if (piece.type === PieceType.Queen) whiteQueens++;
      else if (piece.type === PieceType.Knight || piece.type === PieceType.Bishop) whiteMinorPieces++;
      else if (piece.type === PieceType.Rook) whiteRooks++;
    } else {
      if (piece.type === PieceType.Queen) blackQueens++;
      else if (piece.type === PieceType.Knight || piece.type === PieceType.Bishop) blackMinorPieces++;
      else if (piece.type === PieceType.Rook) blackRooks++;
    }
  }
  
  // No queens = endgame
  if (whiteQueens === 0 && blackQueens === 0) {
    return true;
  }
  
  // Queen but no other pieces (or only one minor piece) = endgame
  if (whiteQueens > 0 && whiteRooks === 0 && whiteMinorPieces <= 1 &&
      blackQueens > 0 && blackRooks === 0 && blackMinorPieces <= 1) {
    return true;
  }
  
  return false;
}

/**
 * Evaluates material balance (piece values only, no positional)
 * @param state Current game state
 * @returns Material score in centipawns (positive = white ahead, negative = black ahead)
 */
export function evaluateMaterial(state: GameState): number {
  let score = 0;
  
  for (let square = 0; square < 64; square++) {
    const piece = state.board[square];
    if (!piece) continue;
    
    const value = PIECE_VALUES[piece.type];
    score += piece.color === Color.White ? value : -value;
  }
  
  return score;
}

/**
 * Evaluates positional factors (piece placement)
 * @param state Current game state
 * @returns Positional score in centipawns (positive = white better, negative = black better)
 */
export function evaluatePosition(state: GameState): number {
  let score = 0;
  const endgame = isEndgame(state);
  
  for (let square = 0; square < 64; square++) {
    const piece = state.board[square];
    if (!piece) continue;
    
    const value = getPieceSquareValue(piece.type, square, piece.color, endgame);
    score += piece.color === Color.White ? value : -value;
  }
  
  return score;
}

/**
 * Evaluates complete position
 * @param state Current game state
 * @returns Total evaluation in centipawns (positive = white ahead, negative = black ahead)
 */
export function evaluate(state: GameState): number {
  // Start with material count
  let score = evaluateMaterial(state);
  
  // Add positional bonuses
  score += evaluatePosition(state);
  
  // TODO: Add more evaluation factors:
  // - Pawn structure (isolated, doubled, passed pawns)
  // - King safety
  // - Mobility (number of legal moves)
  // - Control of center
  // - Piece coordination
  
  return score;
}
